import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { FiPlus, FiEdit2, FiTrash2, FiTag } from 'react-icons/fi';
import { dt, DashLang } from '../../utils/dashboardTranslations';

export default function PromoCodes() {
  const { store, lang } = useOutletContext<{ store: any; lang: DashLang }>();
  const [promos, setPromos] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState({
    code: '', discount_type: 'percentage', discount_value: '',
    min_order_amount: '', max_discount: '', start_date: '', end_date: '',
    usage_limit: '', product_ids: [] as number[], category_ids: [] as number[],
    is_active: true, show_on_store: false,
  });

  useEffect(() => {
    if (store?.id) {
      loadPromos();
      loadCategories();
      loadProducts();
    }
  }, [store?.id]);

  const loadPromos = async () => {
    setLoading(true);
    try { const res = await api.get(`/stores/${store.id}/promo-codes`); setPromos(res.data); }
    finally { setLoading(false); }
  };

  const loadCategories = async () => {
    try {
      const res = await api.get(`/stores/${store.id}/categories`);
      setCategories(res.data || []);
    } catch { setCategories([]); }
  };

  const loadProducts = async () => {
    try {
      const res = await api.get(`/stores/${store.id}/products?per_page=999`);
      const data = res.data?.data || res.data || [];
      setProducts(Array.isArray(data) ? data : []);
    } catch { setProducts([]); }
  };

  const openModal = (promo?: any) => {
    if (promo) {
      setEditing(promo);
      setForm({
        code: promo.code, discount_type: promo.discount_type,
        discount_value: promo.discount_value,
        min_order_amount: promo.min_order_amount || '',
        max_discount: promo.max_discount || '',
        start_date: promo.start_date ? promo.start_date.split('T')[0] : '',
        end_date: promo.end_date ? promo.end_date.split('T')[0] : '',
        usage_limit: promo.usage_limit || '',
        product_ids: promo.product_ids || [],
        category_ids: promo.category_ids || [],
        is_active: promo.is_active, show_on_store: promo.show_on_store,
      });
    } else {
      setEditing(null);
      setForm({
        code: '', discount_type: 'percentage', discount_value: '',
        min_order_amount: '', max_discount: '', start_date: '', end_date: '',
        usage_limit: '', product_ids: [], category_ids: [],
        is_active: true, show_on_store: false,
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: any = {
      code: form.code.toUpperCase(),
      discount_type: form.discount_type,
      discount_value: parseFloat(form.discount_value) || 0,
      min_order_amount: form.min_order_amount ? parseFloat(form.min_order_amount as string) : 0,
      max_discount: form.max_discount ? parseFloat(form.max_discount as string) : null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      usage_limit: form.usage_limit ? parseInt(form.usage_limit as string) : null,
      product_ids: form.product_ids.length > 0 ? form.product_ids : null,
      category_ids: form.category_ids.length > 0 ? form.category_ids : null,
      is_active: form.is_active,
      show_on_store: form.show_on_store,
    };

    try {
      if (editing) {
        await api.put(`/stores/${store.id}/promo-codes/${editing.id}`, payload);
        toast.success(dt('promo_updated', lang));
      } else {
        await api.post(`/stores/${store.id}/promo-codes`, payload);
        toast.success(dt('promo_created', lang));
      }
      setShowModal(false);
      loadPromos();
    } catch (err: any) {
      toast.error(err.response?.data?.message || dt('error_saving_promo', lang));
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(dt('delete_promo_confirm', lang))) return;
    try {
      await api.delete(`/stores/${store.id}/promo-codes/${id}`);
      toast.success(dt('promo_deleted', lang));
      loadPromos();
    } catch {
      toast.error(dt('error_deleting_promo', lang));
    }
  };

  const getStatus = (p: any) => {
    if (p.end_date && new Date(p.end_date) < new Date()) return { label: dt('expired', lang), cls: 'badge-cancelled' };
    if (p.usage_limit && p.usage_count >= p.usage_limit) return { label: dt('depleted', lang), cls: 'badge-cancelled' };
    if (p.is_active) return { label: dt('active', lang), cls: 'badge-ready' };
    return { label: dt('inactive', lang), cls: 'badge-pending' };
  };

  const toggleMulti = (arr: number[], id: number) =>
    arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];

  if (!store) return null;

  return (
    <div>
      <div className="page-header">
        <h1>{dt('promo_codes', lang)}</h1>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <FiPlus /> {dt('add_promo', lang)}
        </button>
      </div>

      {loading ? (
        <div className="empty-state"><p>{dt('loading', lang)}...</p></div>
      ) : promos.length === 0 ? (
        <div className="empty-state">
          <FiTag />
          <h3>{dt('no_promos', lang)}</h3>
          <p>{dt('add_first_promo', lang)}</p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => openModal()}>
            <FiPlus /> {dt('add_promo', lang)}
          </button>
        </div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>{dt('code', lang)}</th>
                <th>{dt('discount_type', lang)}</th>
                <th>{dt('discount_value', lang)}</th>
                <th>{dt('min_order_amount', lang)}</th>
                <th>{dt('usage', lang)}</th>
                <th>{dt('show_on_store', lang)}</th>
                <th>{dt('status', lang)}</th>
                <th>{dt('actions', lang)}</th>
              </tr>
            </thead>
            <tbody>
              {promos.map((p) => {
                const status = getStatus(p);
                return (
                  <tr key={p.id}>
                    <td><strong style={{ fontFamily: 'monospace', letterSpacing: 1 }}>{p.code}</strong></td>
                    <td>{p.discount_type === 'percentage' ? dt('percentage', lang) : dt('fixed_amount', lang)}</td>
                    <td><strong>{p.discount_type === 'percentage' ? `${p.discount_value}%` : `${store.currency_symbol} ${Number(p.discount_value).toFixed(3)}`}</strong></td>
                    <td>{p.min_order_amount > 0 ? `${store.currency_symbol} ${Number(p.min_order_amount).toFixed(3)}` : '-'}</td>
                    <td>{p.usage_count}{p.usage_limit ? ` / ${p.usage_limit}` : ` / ${dt('unlimited', lang)}`}</td>
                    <td><span className={`badge ${p.show_on_store ? 'badge-confirmed' : 'badge-pending'}`}>{p.show_on_store ? dt('visible', lang) : dt('hidden', lang)}</span></td>
                    <td><span className={`badge ${status.cls}`}>{status.label}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-sm btn-white" onClick={() => openModal(p)}><FiEdit2 /></button>
                        <button className="btn btn-sm btn-white" onClick={() => handleDelete(p.id)} style={{ color: 'var(--danger)' }}><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 650 }}>
            <h2>{editing ? dt('edit_promo', lang) : dt('add_promo', lang)}</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">{dt('code', lang)} *</label>
                  <input className="form-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase() })} required dir="ltr" style={{ fontFamily: 'monospace', letterSpacing: 1, textAlign: 'left' }} placeholder="e.g. SAVE20" />
                </div>
                <div className="form-group">
                  <label className="form-label">{dt('discount_type', lang)} *</label>
                  <select className="form-input" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}>
                    <option value="percentage">{dt('percentage', lang)} (%)</option>
                    <option value="fixed">{dt('fixed_amount', lang)} ({store.currency_symbol})</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">{dt('discount_value', lang)} * {form.discount_type === 'percentage' ? '(%)' : `(${store.currency_symbol})`}</label>
                  <input type="number" step="0.001" className="form-input" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} required />
                </div>
                {form.discount_type === 'percentage' && (
                  <div className="form-group">
                    <label className="form-label">{dt('max_discount', lang)} ({store.currency_symbol})</label>
                    <input type="number" step="0.001" className="form-input" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} placeholder={dt('unlimited', lang)} />
                  </div>
                )}
                {form.discount_type !== 'percentage' && <div />}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">{dt('min_order_amount', lang)} ({store.currency_symbol})</label>
                  <input type="number" step="0.001" className="form-input" value={form.min_order_amount} onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">{dt('usage_limit', lang)}</label>
                  <input type="number" className="form-input" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} placeholder={dt('unlimited', lang)} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">{dt('start_date', lang)}</label>
                  <input type="date" className="form-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">{dt('end_date', lang)}</label>
                  <input type="date" className="form-input" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                </div>
              </div>

              {/* Product & Category selection */}
              <div className="form-group">
                <label className="form-label">
                  {lang === 'ar' ? 'تطبيق على' : 'Apply To'}
                  <span style={{ fontSize: 12, color: 'var(--gray-400)', marginInlineStart: 6 }}>
                    {form.product_ids.length === 0 && form.category_ids.length === 0
                      ? `(${lang === 'ar' ? 'جميع المنتجات' : 'All Products'})`
                      : `(${form.category_ids.length} ${lang === 'ar' ? 'فئة' : 'cat'}, ${form.product_ids.length} ${lang === 'ar' ? 'منتج' : 'products'})`}
                  </span>
                </label>

                {/* Category chips */}
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 6 }}>{lang === 'ar' ? 'الفئات' : 'Categories'}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    <label style={{ padding: '5px 12px', borderRadius: 50, border: '1px solid var(--gray-200)', cursor: 'pointer', fontSize: 12, background: form.category_ids.length === 0 ? 'var(--gray-100)' : 'white', color: 'var(--gray-500)' }}
                      onClick={() => setForm({ ...form, category_ids: [], product_ids: [] })}>
                      {lang === 'ar' ? 'الكل' : 'All'}
                    </label>
                    {categories.map((c: any) => {
                      const isSelected = form.category_ids.includes(c.id);
                      return (
                        <label key={c.id} style={{ padding: '5px 12px', borderRadius: 50, border: '1px solid', borderColor: isSelected ? 'var(--primary)' : 'var(--gray-200)', cursor: 'pointer', fontSize: 12, background: isSelected ? 'var(--primary)' : 'white', color: isSelected ? 'white' : 'var(--gray-600)' }}
                          onClick={() => {
                            const newCats = toggleMulti(form.category_ids, c.id);
                            // Auto-select/deselect products in this category
                            const catProductIds = products.filter(p => p.category_id === c.id).map(p => p.id);
                            let newProds = [...form.product_ids];
                            if (isSelected) {
                              newProds = newProds.filter(id => !catProductIds.includes(id));
                            } else {
                              catProductIds.forEach(id => { if (!newProds.includes(id)) newProds.push(id); });
                            }
                            setForm({ ...form, category_ids: newCats, product_ids: newProds });
                          }}>
                          {c.name}{c.name_ar ? ` — ${c.name_ar}` : ''}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Products grouped by category */}
                <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, maxHeight: 250, overflowY: 'auto' }}>
                  {/* Select all / none */}
                  <div style={{ display: 'flex', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--gray-100)', background: 'var(--gray-50)' }}>
                    <button type="button" onClick={() => setForm({ ...form, product_ids: products.map(p => p.id) })} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--primary)', fontFamily: 'inherit' }}>
                      {lang === 'ar' ? 'تحديد الكل' : 'Select All'}
                    </button>
                    <span style={{ color: 'var(--gray-300)' }}>|</span>
                    <button type="button" onClick={() => setForm({ ...form, product_ids: [] })} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--gray-500)', fontFamily: 'inherit' }}>
                      {lang === 'ar' ? 'إلغاء الكل' : 'Clear All'}
                    </button>
                    <span style={{ marginInlineStart: 'auto', fontSize: 11, color: 'var(--gray-400)' }}>
                      {form.product_ids.length}/{products.length}
                    </span>
                  </div>
                  {categories.filter(c => products.some(p => p.category_id === c.id)).map((cat: any) => {
                    const catProducts = products.filter(p => p.category_id === cat.id);
                    const allSelected = catProducts.every(p => form.product_ids.includes(p.id));
                    return (
                      <div key={cat.id}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-100)', cursor: 'pointer' }}
                          onClick={() => {
                            const ids = catProducts.map(p => p.id);
                            if (allSelected) {
                              setForm({ ...form, product_ids: form.product_ids.filter(id => !ids.includes(id)) });
                            } else {
                              const newIds = [...form.product_ids];
                              ids.forEach(id => { if (!newIds.includes(id)) newIds.push(id); });
                              setForm({ ...form, product_ids: newIds });
                            }
                          }}>
                          <input type="checkbox" checked={allSelected} readOnly style={{ accentColor: 'var(--primary)' }} />
                          <strong style={{ fontSize: 12 }}>{cat.name}</strong>
                          <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>({catProducts.length})</span>
                        </div>
                        {catProducts.map((p: any) => (
                          <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px 5px 28px', cursor: 'pointer', borderBottom: '1px solid var(--gray-50)', fontSize: 13 }}>
                            <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => setForm({ ...form, product_ids: toggleMulti(form.product_ids, p.id) })} style={{ accentColor: 'var(--primary)' }} />
                            {p.name}
                            {p.name_ar && <span style={{ color: 'var(--gray-400)', fontSize: 11 }}>{p.name_ar}</span>}
                          </label>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                  {dt('active', lang)}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.show_on_store} onChange={(e) => setForm({ ...form, show_on_store: e.target.checked })} />
                  {dt('show_on_store', lang)}
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-white" onClick={() => setShowModal(false)}>{dt('cancel', lang)}</button>
                <button type="submit" className="btn btn-primary">{editing ? dt('update', lang) : dt('create', lang)}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
